import {Auth} from '@aws-amplify/auth';
import {useNavigate} from 'react-router-dom';
import {Cache} from 'aws-amplify';
import {Account} from '../types';
import {ActivityEventsService} from '../../debug-mode/activity-events/services/ActivityEventsService';
import {useAccounts} from './useAccounts';
import {checkAccountSession} from '../../account-sessions/services/AccountSessionService';

export const useAccountActions = () => {
    const navigate = useNavigate();
    const {handleUpdateAccount, handleDeleteAccount} = useAccounts();

    const handleOAuth2Redirect = async (account: Account, cachedAuth: any) => {
        ActivityEventsService.add(
            'OAuth2 Direct Redirect Started',
            `Handling OAuth2 redirect for account: ${account.emailAddress}, client_id: ${cachedAuth.clientId}`,
            account.emailAddress
        );
        
        try {
            // Configure Amplify for this account
            Auth.configure({
                userPoolId: account.cognitoUserPoolId,
                userPoolWebClientId: account.appClientId,
            });

            // Check session for this account
            const sessionInfo = await checkAccountSession(account);
            
            if (sessionInfo.isActive && !sessionInfo.isExpired && sessionInfo.session) {
                // Build OAuth2 response URL with tokens
                const tokens = {
                    idToken: sessionInfo.session.getIdToken().getJwtToken(),
                    accessToken: sessionInfo.session.getAccessToken().getJwtToken(),
                    tokenType: 'Bearer',
                    expiresIn: 3600
                };
                
                const responseUrl = `${cachedAuth.redirectUrl}#id_token=${tokens.idToken}&access_token=${tokens.accessToken}&token_type=${tokens.tokenType}&expires_in=${tokens.expiresIn}&state=${cachedAuth.state || ''}`;
                
                ActivityEventsService.add(
                    'OAuth2 Direct Redirect Successful',
                    `OAuth2 tokens generated, redirecting to: ${cachedAuth.redirectUrl}`,
                    account.emailAddress
                );
                
                // Clear cache and redirect
                Cache.removeItem('authorization');
                window.location.assign(responseUrl);
            } else {
                ActivityEventsService.add(
                    'OAuth2 Session Validation Failed',
                    `Session invalid for account: ${account.emailAddress} (active: ${sessionInfo.isActive}, expired: ${sessionInfo.isExpired})`,
                    account.emailAddress
                );
                // Session invalid, redirect to sign-in
                navigate(`/accounts/${account.id}/method-selection`);
            }
        } catch (error) {
            ActivityEventsService.add(
                'OAuth2 Direct Redirect Failed',
                `OAuth2 redirect failed: ${error instanceof Error ? error.message : String(error)}`,
                account.emailAddress
            );
            console.error('OAuth2 redirect error:', error);
            // Fallback to sign-in
            navigate(`/accounts/${account.id}/method-selection`);
        }
    };

    const redirectToSignIn = (account: Account) => {
        ActivityEventsService.add(
            'Authentication Required',
            `User needs to authenticate for: ${account.emailAddress}`,
            account.emailAddress
        );
        
        // Check if we have cached OAuth2 parameters
        const cachedAuth = Cache.getItem('authorization');
        if (cachedAuth) {
            // Store the selected account for OAuth2 flow (OAuth2Service will be smart about client_id matching)
            Cache.setItem('selectedAccount', account);
        }
        
        navigate(`/accounts/${account.id}/method-selection`);
    };

    const handleContinue = async (account: Account) => {
        ActivityEventsService.add(
            'Account Selected',
            `Selected account: ${account.emailAddress}`,
            account.emailAddress
        );

        // Check if we have cached OAuth2 parameters first
        const cachedAuth = Cache.getItem('authorization');
        if (cachedAuth && cachedAuth.redirectUrl) {
            ActivityEventsService.add(
                'Account Selected for OAuth2 Flow',
                `Account ${account.emailAddress} selected for OAuth2 flow (requested client_id: ${cachedAuth.clientId}, account client_id: ${account.appClientId})`,
                account.emailAddress
            );
            // Handle OAuth2 flow directly - let the OAuth2 service be smart about client_id matching
            handleOAuth2Redirect(account, cachedAuth);
            return;
        }

        // Default flow - check session for regular redirect
        const sessionInfo = await checkAccountSession(account);
        if (sessionInfo.isActive && !sessionInfo.isExpired) {
            navigate(`/accounts/${account.id}/redirect`);
        } else {
            redirectToSignIn(account);
        }
    };

    const handleSignOutAndForget = async (account: Account) => {
        handleDeleteAccount(account.id);

        Auth.configure({
            userPoolId: account.cognitoUserPoolId,
            userPoolWebClientId: account.appClientId,
        });

        try {
            await Auth.signOut({global: true});
        } catch (e) {
            await Auth.signOut();
        }

        ActivityEventsService.add(
            'User Signed Out',
            `Signed out from: ${account.emailAddress}`,
            account.emailAddress
        );

        return false;
    };

    const handleSignOut = async (account: Account) => {
        Auth.configure({
            userPoolId: account.cognitoUserPoolId,
            userPoolWebClientId: account.appClientId,
        });

        try {
            await Auth.signOut({global: true});
        } catch (e) {
            await Auth.signOut();
        }

        ActivityEventsService.add(
            'User Signed Out',
            `Signed out from: ${account.emailAddress}`,
            account.emailAddress
        );

        const updated = Object.assign({}, account, {updatedAt: new Date().toISOString()});
        handleUpdateAccount(account.id, updated);

        return false;
    };

    const handleHideAccount = async (account: Account) => {
        const updated = Object.assign({}, account, {isVisible: 'no'});
        handleUpdateAccount(account.id, updated);
        return false;
    };

    return {
        handleContinue,
        handleSignOut,
        handleSignOutAndForget,
        handleHideAccount,
    };
};
